"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import QRTypeSelector, { type QRCodeType } from "./QRTypeSelector"
import QRHistory, { type QRHistoryItem } from "./QRHistory"
import { QRCodeSVG } from "qrcode.react"
import jsPDF from "jspdf"

// Import all form components
import URLForm from "./qr-forms/URLForm"
import TextForm from "./qr-forms/TextForm"
import EmailForm from "./qr-forms/EmailForm"
import PhoneForm from "./qr-forms/PhoneForm"
import SMSForm from "./qr-forms/SMSForm"
import WhatsAppForm from "./qr-forms/WhatsAppForm"
import WiFiForm from "./qr-forms/WiFiForm"
import VCardForm from "./qr-forms/VCardForm"
import LocationForm from "./qr-forms/LocationForm"

const QRCodeGenerator: React.FC = () => {
  // QR Code type state
  const [qrType, setQrType] = useState<QRCodeType>("url")

  // QR Code appearance state
  const [qrSize, setQrSize] = useState(256)
  const [qrColor, setQrColor] = useState("#000000")
  const [bgColor, setBgColor] = useState("#ffffff")
  const [includeMargin, setIncludeMargin] = useState(true)
  const [errorCorrectionLevel, setErrorCorrectionLevel] = useState("M")

  // Logo settings
  const defaultLogoUrl = "/images/badkul-logo.jpeg"
  const [customLogo, setCustomLogo] = useState<string | null>(null)
  const [logoSize, setLogoSize] = useState(20) // percentage of QR code size
  const [logoError, setLogoError] = useState<string | null>(null)

  // Get current logo URL (custom or default)
  const getCurrentLogoUrl = () => customLogo || defaultLogoUrl

  // History state
  const [history, setHistory] = useState<QRHistoryItem[]>([])

  // Form states for different QR types
  const [urlValue, setUrlValue] = useState("https://badkultechnology.com")
  const [textValue, setTextValue] = useState("")

  const [emailAddress, setEmailAddress] = useState("")
  const [emailSubject, setEmailSubject] = useState("")
  const [emailBody, setEmailBody] = useState("")

  const [phoneNumber, setPhoneNumber] = useState("")

  const [smsPhone, setSmsPhone] = useState("")
  const [smsMessage, setSmsMessage] = useState("")

  // WhatsApp state
  const [whatsappPhone, setWhatsappPhone] = useState("")
  const [whatsappMessage, setWhatsappMessage] = useState("")

  const [wifiSsid, setWifiSsid] = useState("")
  const [wifiPassword, setWifiPassword] = useState("")
  const [wifiEncryption, setWifiEncryption] = useState<"WPA" | "WEP" | "nopass">("WPA")
  const [wifiHidden, setWifiHidden] = useState(false)

  const [vcardFirstName, setVcardFirstName] = useState("")
  const [vcardLastName, setVcardLastName] = useState("")
  const [vcardOrganization, setVcardOrganization] = useState("")
  const [vcardTitle, setVcardTitle] = useState("")
  const [vcardEmail, setVcardEmail] = useState("")
  const [vcardPhone, setVcardPhone] = useState("")
  const [vcardWebsite, setVcardWebsite] = useState("")
  const [vcardAddress, setVcardAddress] = useState("")

  const [latitude, setLatitude] = useState("")
  const [longitude, setLongitude] = useState("")

  const qrRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem("qrCodeHistory")
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch (e) {
        console.error("Failed to parse QR code history", e)
      }
    }
  }, [])

  // Save history to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("qrCodeHistory", JSON.stringify(history))
  }, [history])

  // Handle logo file upload
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setLogoError("Please upload an image file (PNG, JPG, GIF, etc.)")
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Image size should be less than 2MB")
      return
    }

    setLogoError(null)
    const reader = new FileReader()
    reader.onload = (event) => {
      if (event.target?.result) {
        setCustomLogo(event.target.result as string)
      }
    }
    reader.readAsDataURL(file)
  }

  // Remove custom logo and revert to default
  const removeCustomLogo = () => {
    setCustomLogo(null)
    setLogoError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Generate QR code value based on selected type and form values
  const getQRCodeValue = (): string => {
    switch (qrType) {
      case "url":
        return urlValue
      case "text":
        return textValue
      case "email":
        let emailStr = `mailto:${emailAddress}`
        if (emailSubject) emailStr += `?subject=${encodeURIComponent(emailSubject)}`
        if (emailBody) emailStr += `${emailSubject ? "&" : "?"}body=${encodeURIComponent(emailBody)}`
        return emailStr
      case "phone":
        return `tel:${phoneNumber}`
      case "sms":
        let smsStr = `sms:${smsPhone}`
        if (smsMessage) smsStr += `?body=${encodeURIComponent(smsMessage)}`
        return smsStr
      case "whatsapp":
        // Remove the "+1" prefix if it exists and strip any non-numeric characters
        const cleanedPhone = whatsappPhone.replace(/^\+1/, "").replace(/[^0-9]/g, "")
        let whatsappStr = `https://wa.me/${cleanedPhone}`
        if (whatsappMessage) whatsappStr += `?text=${encodeURIComponent(whatsappMessage)}`
        return whatsappStr
      case "wifi":
        return `WIFI:T:${wifiEncryption};S:${wifiSsid};P:${wifiPassword};H:${wifiHidden ? "true" : "false"};;`
      case "vcard":
        return `BEGIN:VCARD
VERSION:3.0
N:${vcardLastName};${vcardFirstName};;;
FN:${vcardFirstName} ${vcardLastName}
ORG:${vcardOrganization}
TITLE:${vcardTitle}
EMAIL:${vcardEmail}
TEL:${vcardPhone}
URL:${vcardWebsite}
ADR:;;${vcardAddress};;;;
END:VCARD`
      case "location":
        return `geo:${latitude},${longitude}`
      default:
        return urlValue
    }
  }

  // Get a display value for the history
  const getDisplayValue = (): string => {
    switch (qrType) {
      case "url":
        return urlValue
      case "text":
        return textValue
      case "email":
        return emailAddress
      case "phone":
        return phoneNumber
      case "sms":
        return smsPhone
      case "whatsapp":
        return whatsappPhone
      case "wifi":
        return wifiSsid
      case "vcard":
        return `${vcardFirstName} ${vcardLastName}`
      case "location":
        return `${latitude}, ${longitude}`
      default:
        return urlValue
    }
  }

  // Helper function to generate QR code canvas with logo
  const generateQRCanvas = async (): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        reject(new Error("Could not get canvas context"))
        return
      }

      canvas.width = qrSize
      canvas.height = qrSize

      // Get the SVG element
      const svg = qrRef.current?.querySelector("svg")
      if (!svg) {
        reject(new Error("Could not find SVG element"))
        return
      }

      const svgData = new XMLSerializer().serializeToString(svg)
      const qrImg = new Image()

      qrImg.onload = () => {
        // Draw the QR code
        ctx.drawImage(qrImg, 0, 0, qrSize, qrSize)

        // Load and draw the logo
        const logoUrl = getCurrentLogoUrl()
        if (logoUrl) {
          const logoImg = new Image()
          logoImg.onload = () => {
            const logoSizePx = (qrSize * logoSize) / 100
            const logoX = (qrSize - logoSizePx) / 2
            const logoY = (qrSize - logoSizePx) / 2

            // Draw white background for logo
            ctx.fillStyle = "white"
            ctx.fillRect(logoX - 5, logoY - 5, logoSizePx + 10, logoSizePx + 10)

            // Draw logo
            ctx.drawImage(logoImg, logoX, logoY, logoSizePx, logoSizePx)
            resolve(canvas)
          }
          logoImg.onerror = () => {
            // If logo fails to load, return QR code without logo
            resolve(canvas)
          }
          logoImg.crossOrigin = "anonymous"
          logoImg.src = logoUrl
        } else {
          resolve(canvas)
        }
      }

      qrImg.onerror = () => reject(new Error("Failed to load QR code image"))
      qrImg.crossOrigin = "anonymous"
      qrImg.src = `data:image/svg+xml;base64,${btoa(svgData)}`
    })
  }

  // Download QR code as PNG
  const downloadQRCode = async () => {
    try {
      const canvas = await generateQRCanvas()
      const pngFile = canvas.toDataURL("image/png")

      // Add to history
      const newHistoryItem: QRHistoryItem = {
        id: Date.now().toString(),
        type: qrType,
        value: getDisplayValue(),
        timestamp: Date.now(),
        preview: pngFile,
      }

      setHistory((prev) => [newHistoryItem, ...prev.slice(0, 9)]) // Keep only the 10 most recent

      // Download the file
      const downloadLink = document.createElement("a")
      downloadLink.download = `badkul-qrcode-${Date.now()}.png`
      downloadLink.href = pngFile
      downloadLink.click()
    } catch (error) {
      console.error("Error downloading QR code:", error)
      alert("Error downloading QR code. Please try again.")
    }
  }

  // Export QR code as PDF
  const exportToPDF = async () => {
    try {
      const canvas = await generateQRCanvas()
      const imgData = canvas.toDataURL("image/png")

      // Create PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      })

      // Calculate dimensions to center the QR code
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const qrSizeMM = 80 // QR code size in mm
      const x = (pdfWidth - qrSizeMM) / 2
      const y = (pdfHeight - qrSizeMM) / 2 - 20

      // Add title
      pdf.setFontSize(20)
      pdf.setTextColor(66, 133, 244) // Blue color matching the theme
      pdf.text("Badkul Technology", pdfWidth / 2, 30, { align: "center" })

      pdf.setFontSize(14)
      pdf.setTextColor(0, 0, 0)
      pdf.text("QR Code", pdfWidth / 2, 45, { align: "center" })

      // Add QR code
      pdf.addImage(imgData, "PNG", x, y, qrSizeMM, qrSizeMM)

      // Add QR code details
      pdf.setFontSize(10)
      pdf.setTextColor(100, 100, 100)
      const details = [
        `Type: ${qrType.toUpperCase()}`,
        `Content: ${getDisplayValue()}`,
        `Generated: ${new Date().toLocaleString()}`,
      ]

      details.forEach((detail, index) => {
        pdf.text(detail, pdfWidth / 2, y + qrSizeMM + 20 + index * 8, { align: "center" })
      })

      // Add footer
      pdf.setFontSize(8)
      pdf.text("Generated by Badkul Technology QR Generator", pdfWidth / 2, pdfHeight - 20, { align: "center" })

      // Save the PDF
      pdf.save(`badkul-qrcode-${Date.now()}.pdf`)

      // Add to history
      const newHistoryItem: QRHistoryItem = {
        id: Date.now().toString(),
        type: qrType,
        value: getDisplayValue(),
        timestamp: Date.now(),
        preview: imgData,
      }

      setHistory((prev) => [newHistoryItem, ...prev.slice(0, 9)])
    } catch (error) {
      console.error("Error exporting to PDF:", error)
      alert("Error exporting to PDF. Please try again.")
    }
  }

  // Clear history
  const clearHistory = () => {
    setHistory([])
    localStorage.removeItem("qrCodeHistory")
  }

  // Load a QR code from history
  const loadFromHistory = (item: QRHistoryItem) => {
    setQrType(item.type as QRCodeType)

    // Set the appropriate form values based on the type
    switch (item.type) {
      case "url":
        setUrlValue(item.value)
        break
      case "text":
        setTextValue(item.value)
        break
      // Add other types as needed
    }
  }

  // Render the appropriate form based on QR code type
  const renderForm = () => {
    switch (qrType) {
      case "url":
        return <URLForm url={urlValue} onChange={setUrlValue} />
      case "text":
        return <TextForm text={textValue} onChange={setTextValue} />
      case "email":
        return (
          <EmailForm
            email={emailAddress}
            subject={emailSubject}
            body={emailBody}
            onEmailChange={setEmailAddress}
            onSubjectChange={setEmailSubject}
            onBodyChange={setEmailBody}
          />
        )
      case "phone":
        return <PhoneForm phone={phoneNumber} onChange={setPhoneNumber} />
      case "sms":
        return (
          <SMSForm phone={smsPhone} message={smsMessage} onPhoneChange={setSmsPhone} onMessageChange={setSmsMessage} />
        )
      case "whatsapp":
        return (
          <WhatsAppForm
            phone={whatsappPhone}
            message={whatsappMessage}
            onPhoneChange={setWhatsappPhone}
            onMessageChange={setWhatsappMessage}
          />
        )
      case "wifi":
        return (
          <WiFiForm
            ssid={wifiSsid}
            password={wifiPassword}
            encryption={wifiEncryption}
            hidden={wifiHidden}
            onSsidChange={setWifiSsid}
            onPasswordChange={setWifiPassword}
            onEncryptionChange={setWifiEncryption}
            onHiddenChange={setWifiHidden}
          />
        )
      case "vcard":
        return (
          <VCardForm
            firstName={vcardFirstName}
            lastName={vcardLastName}
            organization={vcardOrganization}
            title={vcardTitle}
            email={vcardEmail}
            phone={vcardPhone}
            website={vcardWebsite}
            address={vcardAddress}
            onFirstNameChange={setVcardFirstName}
            onLastNameChange={setVcardLastName}
            onOrganizationChange={setVcardOrganization}
            onTitleChange={setVcardTitle}
            onEmailChange={setVcardEmail}
            onPhoneChange={setVcardPhone}
            onWebsiteChange={setVcardWebsite}
            onAddressChange={setVcardAddress}
          />
        )
      case "location":
        return (
          <LocationForm
            latitude={latitude}
            longitude={longitude}
            onLatitudeChange={setLatitude}
            onLongitudeChange={setLongitude}
          />
        )
      default:
        return <URLForm url={urlValue} onChange={setUrlValue} />
    }
  }

  return (
    <div className="qr-generator-container">
      <div className="qr-generator">
        <div className="input-section">
          <QRTypeSelector selectedType={qrType} onTypeChange={setQrType} />

          <div className="form-container">{renderForm()}</div>

          <div className="appearance-section">
            <h3>Appearance</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="size-input">Size:</label>
                <input
                  id="size-input"
                  type="range"
                  min="128"
                  max="512"
                  value={qrSize}
                  onChange={(e) => setQrSize(Number(e.target.value))}
                  className="range-input"
                />
                <span>{qrSize}px</span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="color-input">QR Color:</label>
                <input
                  id="color-input"
                  type="color"
                  value={qrColor}
                  onChange={(e) => setQrColor(e.target.value)}
                  className="color-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bg-color-input">Background:</label>
                <input
                  id="bg-color-input"
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                  className="color-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="margin-input">Include Margin:</label>
                <input
                  id="margin-input"
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                  className="checkbox-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="error-correction">Error Correction:</label>
                <select
                  id="error-correction"
                  value={errorCorrectionLevel}
                  onChange={(e) => setErrorCorrectionLevel(e.target.value)}
                  className="select-input"
                >
                  <option value="L">Low (7%)</option>
                  <option value="M">Medium (15%)</option>
                  <option value="Q">Quartile (25%)</option>
                  <option value="H">High (30%)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="logo-section">
            <h3>Logo</h3>

            <div className="logo-upload-controls">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="file-input"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="file-label">
                📁 Upload Custom Logo
              </label>

              {customLogo && (
                <button onClick={removeCustomLogo} className="remove-logo-btn" type="button">
                  🗑️ Remove Custom Logo
                </button>
              )}
            </div>

            {logoError && <p className="error-message">{logoError}</p>}

            <div className="logo-preview-section">
              <div className="logo-preview">
                <img src={getCurrentLogoUrl() || "/placeholder.svg"} alt="Logo Preview" />
              </div>
              <p className="logo-note">
                {customLogo ? "Custom logo will be embedded in QR codes" : "Default Badkul Technology logo"}
              </p>
            </div>

            <div className="logo-size-control">
              <label htmlFor="logo-size">Logo Size:</label>
              <input
                id="logo-size"
                type="range"
                min="10"
                max="30"
                value={logoSize}
                onChange={(e) => setLogoSize(Number(e.target.value))}
                className="range-input"
              />
              <span>{logoSize}%</span>
            </div>
          </div>

          <button onClick={downloadQRCode} className="download-btn">
            Download PNG
          </button>
        </div>

        <div className="qr-display-container">
          <div className="qr-display" ref={qrRef}>
            <QRCodeSVG
              value={getQRCodeValue()}
              size={qrSize}
              bgColor={bgColor}
              fgColor={qrColor}
              level={errorCorrectionLevel as "L" | "M" | "Q" | "H"}
              includeMargin={includeMargin}
              imageSettings={
                getCurrentLogoUrl()
                  ? {
                      src: getCurrentLogoUrl(),
                      excavate: true,
                      height: (qrSize * logoSize) / 100,
                      width: (qrSize * logoSize) / 100,
                    }
                  : undefined
              }
            />
          </div>

          <div className="qr-info">
            <p>Scan with any QR code reader</p>
            <p className="qr-value">{getDisplayValue()}</p>
          </div>

          <div className="qr-export-actions">
            <button onClick={exportToPDF} className="export-pdf-btn">
              📄 Export to PDF
            </button>
          </div>
        </div>
      </div>

      <QRHistory history={history} onSelect={loadFromHistory} onClear={clearHistory} />
    </div>
  )
}

export default QRCodeGenerator
